import React from 'react';
import styled from 'styled-components';
import { useSchedulesQuery } from '../react-queries/queries';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import BookCoverImage from './BookCoverImage';

const ListWrapper = styled.div`
  border-radius: 0;
  padding: 0;
  margin-bottom: 16px;
  background: #fff url('/today_bg.png') no-repeat left center;
  background-size: auto 100%;
  height: 200px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ItemsInner = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  margin-left: 15px;
  padding: 5px 0;
  gap: 5px;
  background: #fff;
`;

const BottomFill = styled.div`
  flex: 1;
  background: #fff;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  min-height: 60px;
  padding: 0 20px 0 0;
  background: #fff;
`;

const IconBox = styled.div<{ $large?: boolean }>`
  width: ${({ $large }) => ($large ? '60px' : '28px')};
  height: ${({ $large }) => ($large ? '60px' : '28px')};
  flex-shrink: 0;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  padding-left: 10px;
  font-size: 20px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 17px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const Period = styled.div`
  font-size: 13px;
  color: #888;
  margin-left: 7px;
`;

const DDay = styled.div<{ $schType?: number | null }>`
  background: ${({ $schType }) => ($schType === 2 ? '#a67c52' : '#636363')};
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  padding: 2px 4px;
  margin-left: 0;
  white-space: nowrap;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  height: 200px;
  padding: 0;
  margin-left: 15px;
  padding-right: 20px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  margin-bottom: 16px;
  color: #666;
  font-size: 15px;
`;

const EmptyButton = styled.button`
  background: #e5e7eb;
  color: #333;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background: #d1d5db;
  }
`;

interface ScheduleListProps {
  emptyMessage?: string;
  emptyButtonLabel?: string;
  onButtonClick?: () => void;
}

const ScheduleList: React.FC<ScheduleListProps> = ({ 
  emptyMessage = '일정이 없습니다.',
  emptyButtonLabel = '일정 추가하기',
  onButtonClick
}) => {
  const { data: schedules = [], isLoading, error , refetch} = useSchedulesQuery();
  const navigate = useNavigate();

  useEffect(() => {
    refetch();
  }, []);

  if (isLoading) {
    return <div>로딩 중...</div>;
  }

  if (error) {
    return <div>에러가 발생했습니다.</div>;
  }

  return (
    <ListWrapper>
      {schedules.length === 0 ? (
        <EmptyState>
          <EmptyMessage>
            {emptyMessage.split(/(<br\s*\/?>|\n)/i).map((part, index) => {
              if (part.match(/<br\s*\/?>/i)) {
                return <br key={index} />;
              } else if (part === '\n') {
                return <br key={index} />;
              }
              return <span key={index}>{part}</span>;
            })}
          </EmptyMessage>
          <EmptyButton onClick={onButtonClick || (() => {})}>
            {emptyButtonLabel}
          </EmptyButton>
        </EmptyState>
      ) : (
        <ScrollArea>
          <ItemsInner>
            {schedules.map(item => (
              <Item key={item.id} onClick={() => navigate(`/book/${item.bookId}`)}>
                <IconBox $large>
                  <BookCoverImage
                    src={item.imgUrl}
                    alt={item.title}
                    width={60}
                    height={60}
                    fontSize={10}
                    borderRadius={4}
                    boxShadow="none"
                  />
                </IconBox>
                <Info>
                  <TitleRow>
                    <DDay $schType={item.schType}>{item.dDay}</DDay>
                    <Title style={{ marginLeft: 8 }}>{item.title}</Title>
                  </TitleRow>
                  <Period>{item.period}</Period>
                </Info>
              </Item>
            ))}
            <BottomFill />
          </ItemsInner>
        </ScrollArea>
      )}
    </ListWrapper>
  );
};

export default ScheduleList; 